// Container Trader customer assistant: floating chat button + panel on every page.
//
// Server: server/api/assistant.js (Perplexity Agent API with Container Trader-only functions).
// Visibility: GET /api/assistant/status -> mode
//   off  -> nothing renders
//   test -> renders only after the visitor opens any page with ?assistant=test (remembered in
//           localStorage; ?assistant=off forgets it)
//   on   -> renders for everyone
//
// Inquiries: the assistant can only PREPARE an inquiry. The visitor sees a summary card and must
// click Send; that click calls /api/assistant/inquiry with the server-held draft id.
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { assistantChat, assistantSendInquiry, assistantStatus } from '../../util/api';

import css from './CtAssistant.module.css';

const TEST_FLAG_KEY = 'ctAssistantTest';
const SESSION_KEY = 'ctAssistantChat';
const MAX_HISTORY = 20;

const WELCOME =
  "Hi, I'm the Container Trader automated assistant. I can help you find available containers, check delivery quotes on Buy It Now listings, and send questions to our team.\n\nWhat size container are you looking for, and what ZIP code should it go to?";

const TYPE_LABELS = {
  listing_question: 'Listing question',
  looking_for: 'Looking for a container',
  delivery_question: 'Delivery question',
  general_question: 'General question',
  selling_or_dealer: 'Selling / dealer',
};

const OUR_HOST = /^https?:\/\/(www\.)?thecontainertrader\.com(\/|$)/i;

// Only links to our own site become clickable. Anything else stays plain text.
const safeHref = url => {
  if (!url) return null;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  if (OUR_HOST.test(url)) return url;
  // Same site on a test/preview domain (e.g. Render preview) is also ours.
  const origin = typeof window !== 'undefined' ? window.location.origin : null;
  return origin && (url === origin || url.startsWith(`${origin}/`)) ? url : null;
};

const TOKEN_RE = /\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)|\*\*([^*]+)\*\*/g;

const renderInline = (text, keyPrefix) => {
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${i++}`;
    if (m[1]) {
      const href = safeHref(m[2]);
      out.push(
        href ? (
          <a key={key} href={href} className={css.link}>
            {m[1]}
          </a>
        ) : (
          m[1]
        )
      );
    } else if (m[3]) {
      const url = m[3].replace(/[.,;:]+$/, '');
      const trailing = m[3].slice(url.length);
      const href = safeHref(url);
      out.push(
        href ? (
          <a key={key} href={href} className={css.link}>
            {url.replace(/^https?:\/\/(www\.)?/, '')}
          </a>
        ) : (
          url
        )
      );
      if (trailing) out.push(trailing);
    } else if (m[4]) {
      out.push(<strong key={key}>{m[4]}</strong>);
    }
    last = TOKEN_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

export const MessageText = ({ text }) => {
  const blocks = `${text || ''}`.split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n');
    const isList = lines.every(l => /^\s*([-*•]|\d+[.)])\s+/.test(l));
    if (isList) {
      return (
        <ul key={bi} className={css.list}>
          {lines.map((l, li) => (
            <li key={li}>{renderInline(l.replace(/^\s*([-*•]|\d+[.)])\s+/, ''), `${bi}-${li}`)}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={bi} className={css.para}>
        {lines.map((l, li) => (
          <React.Fragment key={li}>
            {li > 0 ? <br /> : null}
            {renderInline(l.replace(/^#+\s*/, ''), `${bi}-${li}`)}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

const DraftCard = ({ draft, status, error, onSend }) => (
  <div className={css.draftCard}>
    <div className={css.draftTitle}>Review your inquiry</div>
    <dl className={css.draftList}>
      <dt>Type</dt>
      <dd>{TYPE_LABELS[draft.inquiryType] || draft.inquiryType}</dd>
      <dt>Name</dt>
      <dd>{draft.name}</dd>
      <dt>Email</dt>
      <dd>{draft.email}</dd>
      <dt>Phone</dt>
      <dd>{draft.phone}</dd>
      <dt>Contact by</dt>
      <dd>{draft.preferredContact}</dd>
      {draft.deliveryZip ? (
        <>
          <dt>Delivery ZIP</dt>
          <dd>{draft.deliveryZip}</dd>
        </>
      ) : null}
      <dt>Details</dt>
      <dd>{draft.requirements}</dd>
      {draft.question ? (
        <>
          <dt>Question</dt>
          <dd>{draft.question}</dd>
        </>
      ) : null}
    </dl>
    {status === 'sent' ? (
      <div className={css.draftSent}>
        Sent. Our team will contact you by {draft.preferredContact}.
      </div>
    ) : (
      <>
        <button
          type="button"
          className={css.sendButton}
          onClick={onSend}
          disabled={status === 'sending'}
        >
          {status === 'sending' ? 'Sending...' : 'Send to Container Trader'}
        </button>
        <div className={css.draftHint}>
          Need a change? Tell the assistant what to fix and it will prepare a new summary.
        </div>
        {error ? <div className={css.error}>{error}</div> : null}
      </>
    )}
  </div>
);

const loadSession = () => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const shouldShow = mode => {
  if (mode === 'on') return true;
  if (mode !== 'test') return false;
  try {
    const param = new URLSearchParams(window.location.search).get('assistant');
    if (param === 'test') window.localStorage.setItem(TEST_FLAG_KEY, '1');
    if (param === 'off') window.localStorage.removeItem(TEST_FLAG_KEY);
    return window.localStorage.getItem(TEST_FLAG_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const CtAssistant = () => {
  const [visible, setVisible] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text, draft?, draftStatus?, draftError?}
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    assistantStatus()
      .then(({ mode }) => {
        if (cancelled || !shouldShow(mode)) return;
        setTestMode(mode === 'test');
        const saved = loadSession();
        if (saved && Array.isArray(saved.messages)) {
          setMessages(saved.messages);
          setConversationId(saved.conversationId || null);
          setOpen(!!saved.open);
        }
        setVisible(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    try {
      window.sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ conversationId, messages: messages.slice(-40), open })
      );
    } catch (e) {
      // storage full or blocked: chat still works for this page view
    }
  }, [visible, conversationId, messages, open]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (!visible) return null;

  const send = () => {
    const text = input.trim();
    if (!text || busy) return;
    const history = messages
      .filter(m => (m.role === 'user' || m.role === 'assistant') && m.text)
      .slice(-MAX_HISTORY)
      .map(m => ({ role: m.role, text: m.text }));
    setMessages(prev => [...prev, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    setError(null);
    assistantChat({ conversationId, message: text, history, pagePath: window.location.pathname })
      .then(res => {
        if (res.conversationId) setConversationId(res.conversationId);
        setMessages(prev => [
          ...prev,
          { role: 'assistant', text: res.reply, draft: res.draft || null, draftStatus: 'ready' },
        ]);
      })
      .catch(e => {
        if (e && e.conversationId) setConversationId(e.conversationId);
        setError((e && e.error) || 'Something went wrong. Please try again.');
      })
      .finally(() => setBusy(false));
  };

  const updateMessage = (index, patch) =>
    setMessages(prev => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));

  const sendDraft = index => {
    const m = messages[index];
    if (!m || !m.draft) return;
    updateMessage(index, { draftStatus: 'sending', draftError: null });
    assistantSendInquiry({ conversationId, draftId: m.draft.draftId })
      .then(() => updateMessage(index, { draftStatus: 'sent' }))
      .catch(e =>
        updateMessage(index, {
          draftStatus: 'ready',
          draftError: (e && e.error) || 'Could not send. Please try again.',
        })
      );
  };

  const reset = () => {
    setMessages([]);
    setConversationId(null);
    setError(null);
  };

  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className={css.root}>
      {open ? (
        <section className={css.panel} role="dialog" aria-label="Container Trader Assistant">
          <header className={css.header}>
            <div>
              <div className={css.title}>Container Trader Assistant</div>
              <div className={css.subtitle}>
                Automated assistant{testMode ? ' · Test version' : ''}
              </div>
            </div>
            <div className={css.headerActions}>
              <button type="button" className={css.textButton} onClick={reset}>
                New chat
              </button>
              <button
                type="button"
                className={css.closeButton}
                onClick={() => setOpen(false)}
                aria-label="Close assistant"
              >
                ×
              </button>
            </div>
          </header>

          <div className={css.messages} ref={listRef} aria-live="polite">
            <div className={classNames(css.bubble, css.assistant)}>
              <MessageText text={WELCOME} />
            </div>
            {messages.map((m, i) => (
              <React.Fragment key={i}>
                <div
                  className={classNames(css.bubble, m.role === 'user' ? css.user : css.assistant)}
                >
                  {m.role === 'user' ? m.text : <MessageText text={m.text} />}
                </div>
                {m.draft ? (
                  <DraftCard
                    draft={m.draft}
                    status={m.draftStatus}
                    error={m.draftError}
                    onSend={() => sendDraft(i)}
                  />
                ) : null}
              </React.Fragment>
            ))}
            {busy ? (
              <div className={classNames(css.bubble, css.assistant, css.typing)}>
                Checking our listings...
              </div>
            ) : null}
            {error ? <div className={css.error}>{error}</div> : null}
          </div>

          <div className={css.composer}>
            <textarea
              ref={inputRef}
              className={css.input}
              rows={2}
              maxLength={1500}
              value={input}
              placeholder="Ask about containers, delivery or buying..."
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Message"
            />
            <button
              type="button"
              className={css.sendMessage}
              onClick={send}
              disabled={busy || !input.trim()}
            >
              Send
            </button>
          </div>
          <div className={css.disclaimer}>
            Answers come only from thecontainertrader.com. Sellers confirm final availability and
            delivery. Don't share card details in chat.
          </div>
        </section>
      ) : null}

      {!open ? (
        <button type="button" className={css.launcher} onClick={() => setOpen(true)}>
          <span className={css.launcherDot} aria-hidden="true" />
          Ask Container Trader
        </button>
      ) : null}
    </div>
  );
};

export default CtAssistant;
